import React from "react";
import { Link } from "react-router-dom";
import Avatar from "../../../common/Avatar";
import EmailLink from "../../../common/CommentList/common/EmailLink/EmailLink";
import PhoneLink from "../../../common/CommentList/common/PhoneLink/PhoneLink";
import useUserCard from "./useUserCard";

const DIVIDER_STYLE = { width: 1, background: "#ececec", margin: "4px 24px", flexShrink: 0 };

const RoleBadge = ({ label, color, bg }) => (
  <span
    className="badge"
    style={{ color, backgroundColor: bg, fontWeight: 600, fontSize: "0.7rem" }}
  >
    {label.toUpperCase()}
  </span>
);

const ContractStatus = ({ status, compact }) => {
  if (status.isOngoing) {
    return (
      <span
        className={compact ? "" : "badge"}
        style={{ color: "#1e9e5b", backgroundColor: compact ? "transparent" : "#e6f7ee", fontWeight: 600, fontSize: compact ? "inherit" : "0.7rem" }}
      >
        {!compact && <i className="fa fa-check-circle mr-1" />}
        En cours
      </span>
    );
  }

  const color = status.isExpired ? "#dc5f45" : "#3e75ff";
  const bg = compact ? "transparent" : status.isExpired ? "#fdeae6" : "#e9f0ff";

  return (
    <span
      className={compact ? "" : "badge"}
      style={{ color, backgroundColor: bg, fontWeight: 600, fontSize: compact ? "inherit" : "0.7rem" }}
    >
      {!compact && <i className={`fa ${status.isExpired ? "fa-exclamation-circle" : "fa-clock-o"} mr-1`} />}
      {status.isExpired ? "Expiré le " : "Jusqu'au "}
      {status.date}
    </span>
  );
};

const DetailRow = ({ icon, label, value, color }) => (
  <div className="d-flex align-items-center justify-content-between">
    <span className="text-muted text-truncate">
      <i className={`fa ${icon} mr-2`} />{label}
    </span>
    <strong className="text-truncate text-right" style={color ? { color } : undefined}>
      {value}
    </strong>
  </div>
);

const InfoRow = ({ icon, label, color, children }) => (
  <div className="d-flex justify-content-between">
    <strong>{icon && <i className={`fa ${icon} mr-2`} />}{label} :</strong>
    <span style={color ? { color } : undefined}>{children}</span>
  </div>
);

const UserCard = ({ user }) => {
  const {
    roleStyle,
    nomSociete,
    genderPrefix,
    fullName,
    createdAt,
    updatedAt,
    contractStatus,
  } = useUserCard(user);

  return (
    <div className="col-12 mb-2">
      <div className="bg-white shadow-sm h-100" style={{ borderRadius: 10, overflow: "hidden" }}>

        {/* Desktop */}
        <div
          className="d-none d-lg-flex align-items-stretch px-3 py-3"
          style={{ borderLeft: `5px solid ${roleStyle.color}` }}
        >
          {/* Identity */}
          <div
            className="d-flex align-items-center"
            style={{ flex: "0 1 38%", minWidth: 0, paddingRight: 16, overflow: "hidden" }}
          >
            <Avatar width={48} height={48} id={user.id} />
            <div className="ml-3" style={{ minWidth: 0 }}>
              <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {genderPrefix && <span className="text-muted mr-1">{genderPrefix}</span>}
                <strong>{fullName}</strong>
                <span className="ml-2"><RoleBadge {...roleStyle} /></span>
              </div>
              <div className="mt-1 text-truncate" style={{ fontSize: "0.85rem" }}>
                <EmailLink email={user.email} />
              </div>
              {user.phone && (
                <div className="text-muted text-truncate" style={{ fontSize: "0.85rem" }}>
                  <PhoneLink phone={user.phone} />
                </div>
              )}
            </div>
          </div>

          <div className="align-self-stretch" style={DIVIDER_STYLE} />

          {/* Dates / company */}
          <div
            className="d-flex flex-column justify-content-center"
            style={{ flex: "0 1 30%", minWidth: 0, fontSize: "0.85rem", gap: 6, overflow: "hidden" }}
          >
            <DetailRow icon="fa-calendar-plus" label="Créé le" value={createdAt} />
            <DetailRow icon="fa-refresh" label="Mis à jour le" value={updatedAt} />
            {nomSociete && (
              <DetailRow icon="fa-building" label="Nom de la société" value={nomSociete} color={roleStyle.color} />
            )}
          </div>

          <div className="align-self-stretch" style={DIVIDER_STYLE} />

          {/* Status & actions */}
          <div
            className="d-flex flex-column align-items-end justify-content-center"
            style={{ flex: "1 1 0%", minWidth: 0, gap: 8 }}
          >
            <ContractStatus status={contractStatus} />
            <Link to={"/users/" + user.id} className="btn btn-sm btn-primary" data-priv="bo_modifyuser">
              <i className="fa fa-pencil mr-1" />
              Modifier
            </Link>
          </div>
        </div>

        {/* Mobile */}
        <div className="d-lg-none">
          <div style={{ height: 8, backgroundColor: roleStyle.color, margin: "-1px -1px 0" }} />
          <div className="p-3 text-center">
            <div className="d-flex justify-content-center mb-2">
              <Avatar width={64} height={64} id={user.id} />
            </div>

            <div>
              {genderPrefix && <span className="text-muted mr-1">{genderPrefix}</span>}
              <strong>{fullName}</strong>
            </div>
            <div className="mt-1 text-muted" style={{ fontSize: "0.85rem" }}>
              <EmailLink email={user.email} />
            </div>
            {user.phone && (
              <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                <PhoneLink phone={user.phone} />
              </div>
            )}
            <div className="mt-2">
              <RoleBadge {...roleStyle} />
            </div>

            <div
              className="mt-3 pt-3 d-flex flex-column text-left"
              style={{ borderTop: "1px solid #f0f0f0", fontSize: "0.85rem", gap: 4 }}
            >
              <InfoRow icon="fa-calendar-plus" label="Créé le">{createdAt}</InfoRow>
              <InfoRow icon="fa-refresh" label="Mis à jour le">{updatedAt}</InfoRow>
              <InfoRow label="Statut du contrat">
                <ContractStatus status={contractStatus} compact />
              </InfoRow>
              {nomSociete && (
                <InfoRow icon="fa-building" label="Nom de la société" color={roleStyle.color}>
                  {nomSociete}
                </InfoRow>
              )}
            </div>

            <Link to={"/users/" + user.id} className="btn btn-primary btn-block mt-3" data-priv="bo_modifyuser">
              Modifier
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default UserCard;
